import React, { useEffect, useState } from "react";
import { api, formatIDR, formatDate, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DateRangeFilter, { useDateFilter } from "@/components/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

const CATEGORIES = ["Operasional", "Gaji Tim", "Transportasi", "Sewa", "Listrik & Air", "Marketing", "Lainnya"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "Operasional", amount: 0, description: "", date: "" });
  const [range, setRange] = useState({ from: "", to: "" });

  const load = () => api.get("/expenses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const byDate = useDateFilter(items, range);
  const tf = useTableFilter(byDate, {
    searchKeys: ["category", "description"],
    defaultSort: { key: "date", dir: "desc" },
  });

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Nominal tidak valid");
    try {
      await api.post("/expenses", {
        ...form,
        amount: Number(form.amount),
        date: form.date || undefined,
      });
      toast.success("Pengeluaran tercatat. Arus kas terupdate.");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus pengeluaran?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const total = tf.rows.reduce((s, x) => s + Number(x.amount || 0), 0);

  return (
    <>
      <PageHeader
        title="Pengeluaran" subtitle="Biaya operasional dan lain-lain, otomatis masuk arus kas"
        testId="page-expenses"
        actions={
          <Button onClick={()=>{setForm({ category: "Operasional", amount: 0, description: "", date: new Date().toISOString().slice(0,10) });setOpen(true);}} data-testid="btn-add-expense" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
            <Plus className="w-4 h-4 mr-2"/> Catat Pengeluaran
          </Button>
        }
      />

      <div className="mb-4"><DateRangeFilter value={range} onChange={setRange}/></div>

      <Card className="p-5 border border-[var(--hotto-border)] rounded-md mb-4 flex items-center justify-between">
        <div>
          <div className="overline">Total Pengeluaran (setelah filter)</div>
          <div className="font-mono-num text-2xl font-bold text-[var(--hotto-destructive)]">{formatIDR(total)}</div>
        </div>
        <div className="text-xs text-slate-500 max-w-xs text-right">{tf.rows.length} transaksi · Semua pengeluaran otomatis mengurangi saldo kas.</div>
      </Card>

      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b border-[var(--hotto-border)]">
          <TableToolbar tf={tf} placeholder="Cari kategori / deskripsi..." testId="expense-search"/>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10">#</TableHead>
              <SortHeader tf={tf} col="date">Tanggal</SortHeader>
              <SortHeader tf={tf} col="category">Kategori</SortHeader>
              <SortHeader tf={tf} col="description">Deskripsi</SortHeader>
              <SortHeader tf={tf} col="amount" className="text-right">Nominal</SortHeader>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tf.rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada pengeluaran.</TableCell></TableRow>}
            {tf.rows.map((x, idx) => (
              <TableRow key={x.id} className="table-row-hover">
                <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                <TableCell className="font-mono-num text-sm">{formatDate(x.date)}</TableCell>
                <TableCell><span className="text-xs uppercase font-semibold text-[var(--hotto-muted)]">{x.category}</span></TableCell>
                <TableCell className="text-slate-600">{x.description || "-"}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold text-[var(--hotto-destructive)]">{formatIDR(x.amount)}</TableCell>
                <TableCell className="text-right"><Button size="icon" variant="ghost" onClick={()=>del(x.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Catat Pengeluaran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kategori</Label>
                <Select value={form.category} onValueChange={(v)=>setForm({...form,category:v})}>
                  <SelectTrigger data-testid="select-category"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} data-testid="input-expense-date"/></div>
            </div>
            <div><Label>Nominal (Rp)</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form,amount:e.target.value})} data-testid="input-amount"/></div>
            <div><Label>Deskripsi</Label><Textarea value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} data-testid="input-description"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="btn-save-expense" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
